import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import history from 'store/history';
import Draggable from 'react-draggable';
import { Slider } from 'material-ui-slider';
import AffiliationCard from 'components/AffiliationCard';
import Avatar from 'components/Avatar';
import {
  BUBBLE_SIZE, DESVIATION, FIRST_LEVEL_INFO_ZOOM, SECOND_LEVEL_INFO_ZOOM, MAX_TAGS,
  MARGIN_OFFSET, MAX_ORBIT_SATELLITES, MARGIN_BUBBLE, SATELLITES_SIZE, ZOOM_WHEEL,
  MAX_INLINE_ELEMENTS, INLINE_ELEMENTS_SIZE,
} from './constants';
import {
  ThemeTablesGrid, ThemeTablesWrapper, Bubble, Satellite,
} from './styles';

function ThemeTablesBubbles(props) {
  const {
    data, literals, onClick, onFullscreen,
  } = props;
  const [heightComponent, setHeightComponent] = useState(0);
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const [bubbles, setBubbles] = useState([]);
  const [bounds, setBounds] = useState({
    left: 0, right: 0, top: 0, bottom: 0,
  });
  const [zoom, setZoom] = useState(50);
  const [scale, setScale] = useState(1.3);
  const [positionDraggable, setPositionDraggable] = useState({ x: 0, y: 0 });
  const [renderNumber, setRenderNumber] = useState(0);
  const [lastSizeParent, setLastSizeParent] = useState({ width: 0, height: 0 });
  const [hasDrag, setHasDrag] = useState(false);

  // Rerender
  useEffect(() => {
    setRenderNumber(renderNumber + 1);
  }, [bubbles, zoom, containerSize, positionDraggable]);

  // Zoom
  const changeZoom = (value) => {
    const newScale = (value * 0.02) + 0.3;
    setZoom(value);
    setScale(newScale);
  };

  // Set sizes and positions
  useEffect(() => {
    changeZoom(75);
    const positions = [];

    const isEmptySpace = (position) => {
      let isEmpty = true;
      _.forEach(positions, (tmp) => {
        const positionLeft = position.left - MARGIN_BUBBLE;
        const positionRight = position.right + MARGIN_BUBBLE;
        const positionTop = position.top - MARGIN_BUBBLE;
        const positionBottom = position.bottom + MARGIN_BUBBLE;

        if ((((tmp.left >= positionLeft && tmp.left <= positionRight)
          || (tmp.right >= positionLeft && tmp.right <= positionRight))
          && ((tmp.top >= positionTop && tmp.top <= positionBottom)
          || (tmp.bottom >= positionTop && tmp.bottom <= positionBottom)))
          || (((positionLeft >= tmp.left && positionLeft <= tmp.right)
          || (positionRight >= tmp.left && positionRight <= tmp.right))
          && ((positionTop >= tmp.top && positionTop <= tmp.bottom)
          || (positionBottom >= tmp.top && positionBottom <= tmp.bottom)))) {
          isEmpty = false;
        }
      });
      return isEmpty;
    };

    const findBubblePosition = (bubble) => {
      const numPositions = positions.length;
      const size = BUBBLE_SIZE[bubble.size];

      const mod = numPositions % 8;
      let dirX = 0;
      let dirY = 0;

      const desv = DESVIATION;
      const desvHalf = DESVIATION / 2;

      switch (mod) {
        case 1:
          // To the right
          dirX = 10 + (Math.random() * desv) - desvHalf;
          dirY = (Math.random() * desv) - desvHalf;
          break;
        case 2:
          // To the left
          dirX = -10 + (Math.random() * desv) - desvHalf;
          dirY = (Math.random() * desv) - desvHalf;
          break;
        case 3:
          // To the bottom
          dirY = 10 + (Math.random() * desv) - desvHalf;
          dirX = (Math.random() * desv) - desvHalf;
          break;
        case 4:
          // To the top
          dirY = -10 + (Math.random() * desv) - desvHalf;
          dirX = (Math.random() * desv) - desvHalf;
          break;
        case 5:
          // To the Noreast
          dirY = -10 + (Math.random() * desv) - desvHalf;
          dirX = 10 + (Math.random() * desv) - desvHalf;
          break;
        case 6:
          // To the Southeast
          dirY = 10 + (Math.random() * desv) - desvHalf;
          dirX = 10 + (Math.random() * desv) - desvHalf;
          break;
        case 7:
          // To the Northwest
          dirY = -10 + (Math.random() * desv) - desvHalf;
          dirX = -10 + (Math.random() * desv) - desvHalf;
          break;
        default:
          // To the southwest
          dirY = 10 + (Math.random() * desv) - desvHalf;
          dirX = -10 + (Math.random() * desv) - desvHalf;
          break;
      }

      const position = {
        top: 0,
        left: 0,
        right: size,
        bottom: size,
      };

      let tries = 0;
      while (!isEmptySpace(position) && tries < 1500) {
        position.top += dirY;
        position.bottom += dirY;
        position.left += dirX;
        position.right += dirX;
        tries += 1;
      }

      positions.push(position);

      return position;
    };
    if (data.length) {
      setBubbles([...data]);

      const newBubbles = _.map(data, (bubble) => {
        const extendBubble = { ...bubble };

        if (typeof extendBubble.size === 'undefined') {
          extendBubble.size = 'xs';
        }

        extendBubble.position = findBubblePosition(extendBubble);

        return extendBubble;
      });

      // Normalize positions
      const minLeft = _.minBy(newBubbles, 'position.left').position.left;
      const maxRight = _.maxBy(newBubbles, 'position.right').position.right;
      const minTop = _.minBy(newBubbles, 'position.top').position.top;
      const maxBottom = _.maxBy(newBubbles, 'position.bottom').position.bottom;

      const increaseLeft = Math.abs(minLeft);
      const increaseTop = Math.abs(minTop);

      const normalizeBubbles = _.map(newBubbles, bubble => ({
        ...bubble,
        position: {
          left: bubble.position.left + increaseLeft + MARGIN_OFFSET,
          right: bubble.position.right + increaseLeft + MARGIN_OFFSET,
          top: bubble.position.top + increaseTop + MARGIN_OFFSET,
          bottom: bubble.position.bottom + increaseTop + MARGIN_OFFSET,
        },
      }));
      setBubbles(normalizeBubbles);

      const newContainerSize = {
        width: maxRight + increaseLeft + (MARGIN_OFFSET * 2),
        height: maxBottom + increaseTop + (MARGIN_OFFSET * 2),
      };

      setContainerSize(newContainerSize);

      const newHeightComponent = window.$('#themeTablesGrid').parent().outerHeight();

      setHeightComponent(newHeightComponent);
    }
  }, [data]);

  // Calculate bounds
  useEffect(() => {
    const newHeightComponent = window.$('.theme_tables_overflow:first').outerHeight();
    const newWidthComponent = window.$('.theme_tables_overflow:first').outerWidth();

    const diffX = (containerSize.width - newWidthComponent);
    const diffY = (containerSize.height - newHeightComponent);

    const scaleX = (containerSize.width * scale - containerSize.width) / 2;
    const scaleY = (containerSize.height * scale - containerSize.height) / 2;

    const left = Math.min(diffX * (-1) - scaleX, scaleX);
    const right = Math.max(diffX * (-1) - scaleX, scaleX);
    const top = Math.min(diffY * (-1) - scaleY, scaleY);
    const bottom = Math.max(diffY * (-1) - scaleY, scaleY);

    const newBounds = {
      left, right, top, bottom,
    };
    setBounds(newBounds);
  }, [containerSize, zoom, lastSizeParent]);

  const printSatellitesInOrbit = (satellites) => {
    const satellitesBubbles = _.map(satellites.slice(0, MAX_ORBIT_SATELLITES), (satellite, key) => {
      const size = 'xs';
      return (
        <Satellite
          className={`sat_${key}`}
          key={satellite.id}
        >
          <Avatar
            firstname={satellite.firstname || satellite.name}
            avatar={satellite.avatar || satellite.image || satellite.logo}
            id={satellite.id}
            size={SATELLITES_SIZE[size]}
          />
        </Satellite>
      );
    });

    if (satellitesBubbles.length < satellites.length) {
      const numSatellite = satellitesBubbles.length;
      const difference = satellites.length - satellitesBubbles.length;
      satellitesBubbles.push(<Satellite key={0} className={`sat_${numSatellite} more_indicator`}>{`+${difference}`}</Satellite>);
    }

    return satellitesBubbles;
  };

  const handleClickBubble = (event) => {
    const target = event.currentTarget;
    const id = parseInt(target.getAttribute('data-id'), 10);
    // history.push(`/theme-table/${id}`);

    onClick(id);
  };

  const handleClickDraggable = (event) => {
    const { target } = event;
    if (typeof onClick === 'function' && !window.$(target).hasClass('theme_bubble') && !window.$(target).parents('.theme_bubble').length) {
      onClick(0);
    }
  };

  const printBubbles = () => {
    if (!bubbles.length) {
      return null;
    }

    return _.map(bubbles, (bubble) => {
      const { color, size, position } = bubble;

      const showSatellitesInOrbit = scale >= SECOND_LEVEL_INFO_ZOOM[size] && bubble.satellites && bubble.satellites.length;
      return (
        <Bubble data-id={bubble.id} key={bubble.id} className={`${size} theme_bubble`} color={color} style={{ top: position.top, left: position.left }} onTouchStart={handleClickBubble} onClick={handleClickBubble} image={bubble.image} highlighted={bubble.highlighted}>
          <div className='opacity' />
          {showSatellitesInOrbit ? printSatellitesInOrbit(bubble.satellites) : null}
          <div className='bubble_content'>
            {scale >= FIRST_LEVEL_INFO_ZOOM[size] ? (
              <div className='name' title={bubble.name}>
                {bubble.name}
                {typeof bubble.isNew !== 'undefined' && bubble.isNew ? (
                  <span className='is_new'>{literals.isNew}</span>
                ) : null}
              </div>) : null}
            {}
          </div>
        </Bubble>
      );
    });
  };

  // Draggabble
  const checkParentSize = () => {
    const newHeightParent = window.$('#themeTablesGrid').parent().outerHeight();
    const newWidthParent = window.$('#themeTablesGrid').parent().outerWidth();

    if (lastSizeParent.width !== newWidthParent || lastSizeParent.height !== newHeightParent) {
      setLastSizeParent({ width: newWidthParent, height: newHeightParent });
    }
  };

  const handleDragStart = () => {
    checkParentSize();
  };

  const handleDragStop = (event, eventData) => {
    const { x, y } = positionDraggable;
    const diff = Math.abs(eventData.x - x) + Math.abs(eventData.y - y);
    setPositionDraggable({ x: eventData.x, y: eventData.y });

    if (diff > 5) {
      setHasDrag(true);

      setTimeout(() => {
        setHasDrag(false);
      }, 200);
    }
  };

  // Check position is out of bounds
  const checkBounds = () => {
    if (bounds && positionDraggable.x && positionDraggable.y && bounds.top !== bounds.bottom && bounds.left !== bounds.right) {
      const newPositionDraggable = {
        y: _.clamp(positionDraggable.y, bounds.top, bounds.bottom),
        x: _.clamp(positionDraggable.x, bounds.left, bounds.right),
      };
      setPositionDraggable(newPositionDraggable);
    }
  };

  useEffect(() => {
    checkBounds();
  }, [bounds]);

  // Set initialposition
  useEffect(() => {
    const newHeightComponent = window.$('#themeTablesGrid').parent().outerHeight();
    const newWidthComponent = window.$('#themeTablesGrid').parent().outerWidth();
    const newX = (containerSize.width * scale - newWidthComponent) / 2;
    const newY = (containerSize.height * scale - newHeightComponent) / 2;
    setPositionDraggable({ x: newX * (-1), y: newY * (-1) });
    checkParentSize();
  }, [containerSize]);

  // Handle wheel
  const handleWheel = (event) => {
    const newZoom = event.deltaY < 0 ? (zoom + ZOOM_WHEEL) : (zoom - ZOOM_WHEEL);
    changeZoom(_.clamp(newZoom, 0, 100));
  };

  const template = useMemo(() => (
    <ThemeTablesGrid id='themeTablesGrid' height={heightComponent} scale={scale} size={containerSize}>
      <div className='theme_tables_overflow' onWheel={handleWheel}>
        <Draggable
          bounds={bounds}
          onStart={handleDragStart}
          onStop={handleDragStop}
          defaultPosition={{ x: positionDraggable.x, y: positionDraggable.y }}
          position={{ x: positionDraggable.x, y: positionDraggable.y }}
          scale={scale}
          onMouseDown={handleClickDraggable}
        >
          <ThemeTablesWrapper scale={scale} size={containerSize}><div>{printBubbles()}</div></ThemeTablesWrapper>
        </Draggable>
      </div>
      <div className='options_wrapper'>
        {onFullscreen ? (
          <button className='btn btn-fullscreen' onClick={onFullscreen}>
            {literals.fullscreen}
            <i className='fas fa-expand' />
          </button>
        ) : null}
        <div className='slider_wrapper'>
          <Slider
            value={zoom}
            aria-labelledby='discrete-slider-small-steps'
            step={5}
            marks
            onChange={changeZoom}
            min={0}
            max={100}
            valueLabelDisplay='auto'
          />
        </div>
      </div>
    </ThemeTablesGrid>
  ), [renderNumber]);

  return template;
}

ThemeTablesBubbles.propTypes = {
  data: PropTypes.array.isRequired,
  literals: PropTypes.object.isRequired,
  onClick: PropTypes.func,
  onFullscreen: PropTypes.func,
};

ThemeTablesBubbles.defaultProps = {
  onClick: null,
  onFullscreen: null,
};

export default ThemeTablesBubbles;



// WEBPACK FOOTER //
// ./src/components/ThemeTablesBubbles/components/index.js